import { useCallback, useRef, useState } from 'react';

import { RectSize } from '../../../../../../common/maths/geometry/rect/rectTypes';

type UseCropSourceSwapFootprintProps = {
    enabled: boolean;
    onEditorReadyChange: (ready: boolean) => void;
};

type UseCropSourceSwapFootprintReturn = {
    /** The footprint to keep the editor box at while the swapped-in source is still loading. */
    pinnedEditorFootprint: RectSize | null;
    /** True from the crop-tool press until the swapped-in source has replaced into Cropper. */
    isCropSourceSwapPending: boolean;
    beginCropSourceSwap: (outgoingFootprint: RectSize) => void;
    /** Wraps the editor-ready callback so a completed swap releases the pin. */
    handleEditorReadyChange: (ready: boolean) => void;
    /** True exactly once after a swap releases the pin, so the caller can reflow Cropper into the new box. */
    consumePendingSwapReflow: () => boolean;
};

/**
 * Pins the editor footprint while the crop tool swaps between the edited render and the full source.
 *
 * The two sources usually have different dimensions, so without the pin the editor box re-fits to the
 * incoming source on the same frame as the crop-tool press — exposing the modal background around the
 * outgoing Cropper (a white flash) before the replacement image has loaded. The pin holds the outgoing
 * footprint until the editor reports ready with the swapped-in source, then releases it and flags one
 * Cropper reflow so the crop geometry re-fits the destination box.
 */
export const useCropSourceSwapFootprint = ({
    enabled,
    onEditorReadyChange,
}: UseCropSourceSwapFootprintProps): UseCropSourceSwapFootprintReturn => {
    const [pinnedEditorFootprint, setPinnedEditorFootprint] = useState<RectSize | null>(null);
    const isSwapPendingRef = useRef(false);
    const pendingReflowRef = useRef(false);

    const beginCropSourceSwap = useCallback(
        (outgoingFootprint: RectSize) => {
            if (!enabled) return;

            isSwapPendingRef.current = true;
            setPinnedEditorFootprint(outgoingFootprint);
        },
        [enabled],
    );

    const handleEditorReadyChange = useCallback(
        (ready: boolean) => {
            onEditorReadyChange(ready);

            if (!ready || !isSwapPendingRef.current) return;

            isSwapPendingRef.current = false;
            pendingReflowRef.current = true;
            setPinnedEditorFootprint(null);
        },
        [onEditorReadyChange],
    );

    const consumePendingSwapReflow = useCallback(() => {
        if (!pendingReflowRef.current) return false;

        pendingReflowRef.current = false;
        return true;
    }, []);

    return {
        pinnedEditorFootprint,
        isCropSourceSwapPending: !!pinnedEditorFootprint,
        beginCropSourceSwap,
        handleEditorReadyChange,
        consumePendingSwapReflow,
    };
};
